import { useMsal } from "@azure/msal-react";
import React, { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useAuth } from "@/hooks/useAuth";
import WidgetLayout from "@/shared/components/WidgetLayout";

import { selectFeatures, selectSelectedRole } from "../store/roleSlice";

/**
 *  Safe Lazy Loader
 */
const safeLazy = (importFn: any, exportName?: string) =>
  React.lazy(async () => {
    const module = await importFn();

    if (exportName) {
      if (!module[exportName]) {
        throw new Error(`Component ${exportName} not found`);
      }
      return { default: module[exportName] };
    }

    if (!module.default) {
      throw new Error("Default export not found");
    }

    return { default: module.default };
  });

type WidgetConfig = {
  component: keyof typeof widgetComponentMap;
  layout: string;
  title?: string;
};

type WidgetProps = {
  userUpn: string;
};

/**
 * Component Map
 */
const widgetComponentMap: Record<
  string,
  React.LazyExoticComponent<React.ComponentType<WidgetProps>>
> = {
  QuickAccessApps: safeLazy(() => import("@/Components/Widgets/QuickAccessApps")),
  ActiveProgramsWidget: safeLazy(() => import("@/features/ActivePrograms"), "ActiveProgramsWidget"),
  TaskPriorities: safeLazy(() => import("@/Components/Widgets/TaskPriorities"), "TaskPriorities"),
  NotificationsPanel: safeLazy(
    () => import("@/features/Notifications/component/NotificationsPanel"),
    "NotificationsPanel"
  ),
  KeyGuidelines: safeLazy(() => import("@/Components/Widgets/KeyGuidelines"), "KeyGuidelines"),
  MeetingsWidget: safeLazy(
    () => import("@/features/Upcoming-meetings/MeetingsWidget"),
    "MeetingsWidget"
  ),
  ImpactTargets: safeLazy(
    () => import("@/features/ImpactTargets/components/ImpactTargets"),
    "ImpactTargets"
  ),
  TeamCalendarWidget: safeLazy(
    () => import("@/Components/TeamCalendar/TeamCalendarWidget"),
    "TeamCalendarWidget"
  ),
  PartnersDirectory: safeLazy(() => import("@/Components/Widgets/partnersDirectory")),
  LearningInsights: safeLazy(() => import("@/Components/Widgets/LearningInsights")),
  AnalyticsWidget: safeLazy(() => import("@/features/AnalyticsInsights/component/AnalyticsWidget")),
  // CompassWidget: safeLazy(() => import("@/backup/Compass/CompassWidget")),
  // PartnerIssuesRisksWidget: safeLazy(
  //   () => import("@/backup/partnerIssuesRisks/PartnerIssuesRisksWidget"),
  //   "PartnerIssuesRisksWidget"
  // ),
  // ProgramMetrics: safeLazy(() => import("@/backup/programMetrics/component/ProgramMetrics")),
  ProgramResources: safeLazy(
    () => import("@/Components/Widgets/ProgramResources"),
    "ProgramResources"
  ),
  OversightWidget: safeLazy(() => import("@/Components/Oversightwidget/OversightWidget")),
  // SubmissionsWidget: safeLazy(
  //   () => import("@/backup/Submission/SubmissionsWidget"),
  //   "SubmissionsWidget"
  // ),
  // PaymentsPipelineWidget: safeLazy(
  //   () => import("@/backup/PaymentsPipelineWidget"),
  //   "PaymentsPipelineWidget"
  // ),
  PartnerSubmissions: safeLazy(
    () => import("@/features/partnerSubmission/component/PartnerSubmissions")
  ),
  DisbursementRequestsWrapper: safeLazy(
    () => import("@/Components/Widgets/DisbursementRequestsWrapper")
  ),
};

const Dashboard: React.FC = () => {
  const { account } = useAuth();
  const { accounts } = useMsal();
  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");
  const userUpn = account?.username || "";
  const { t } = useTranslation();

  const dashboardFeatures = useSelector(selectFeatures);
  const selectedRole = useSelector(selectSelectedRole);

  /**
   * ✅ Widget Config (Memoized instead of useEffect)
   */
  const dashboardWidgetConfig: Record<string, WidgetConfig> = useMemo(() => {
    return {
      "dashboard.quiklinks": { component: "QuickAccessApps", layout: "3" },
      "dashboard.myactiveitems": { component: "ActiveProgramsWidget", layout: "3" },
      "dashboard.taskpriorities": {
        component: "TaskPriorities",
        layout: "1",
      },
      "dashboard.notifications": {
        component: "NotificationsPanel",
        layout: selectedRole === "Partner" ? "3" : "2",
      },
      "dashboard.keyguidelines": { component: "KeyGuidelines", layout: "1" },
      "dashboard.upcomingmeetings": { component: "MeetingsWidget", layout: "3" },
      "dashboard.impacttargets": {
        component: "ImpactTargets",
        layout: selectedRole === "Chiefs" || selectedRole === "FPP" ? "3" : "2",
      },
      "dashboard.teamcalender": {
        component: "TeamCalendarWidget",
        layout: "1",
      },
      "dashboard.partnerdirectory": {
        component: "PartnersDirectory",
        layout: "3",
      },
      "dashboard.learninginsights": {
        component: "LearningInsights",
        layout: "1",
      },
      "dashboard.analyticsinsights": {
        component: "AnalyticsWidget",
        layout: "3",
      },
      "dashboard.programresources": {
        component: "ProgramResources",
        layout: "3",
      },
      "dashboard.oversight": {
        component: "OversightWidget",
        layout: "3",
      },
      "dashboard.submissions": {
        component: "PartnerSubmissions",
        layout: "3",
      },
      "dashboard.partnersubmissions": {
        component: "PartnerSubmissions",
        layout: "2",
      },
      "dashboard.disbursementrequest": {
        component: "DisbursementRequestsWrapper",
        layout: "3",
      },
    };
  }, [selectedRole]);

  /**
   * Build access map (FIXED)
   */
  const featureAccessMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    dashboardFeatures.forEach((feature: { feature_code: string; access: boolean | number }) => {
      map[feature.feature_code] = feature.access === true || feature.access === 1;
    });

    return map;
  }, [dashboardFeatures]);

  /**
   * Render widgets directly in API order
   */
  const renderWidgets = () =>
    dashboardFeatures.map((feature) => {
      if (!featureAccessMap[feature.feature_code]) return null;

      const config = dashboardWidgetConfig[feature.feature_code];

      if (!config) return null;

      const Component = widgetComponentMap[config.component];

      if (!Component) {
        return null;
      }

      return (
        <WidgetLayout key={feature.feature_code} layout={config.layout} title={config.title}>
          <Component userUpn={userUpn} />
        </WidgetLayout>
      );
    });

  return (
    <main className="bg-[rgb(246,244,239)] text-[var(--text-color)]">
      <div className="px-6 pt-1 pb-2">
        <div className="bg-white rounded-xl shadow-sm px-6 py-3 border border-gray-200">
          <h2 className="text-lg font-semibold">
            <span>
              {t("components.dashboard.welcomeMessage")} {displayName}!
            </span>
          </h2>
        </div>
      </div>
      <section className="dashboard-grid grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 px-6 py-4">
        <Suspense fallback={<div>Loading dashboard...</div>}>{renderWidgets()}</Suspense>
      </section>
    </main>
  );
};

export default Dashboard;
